import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Trash2, Filter, ExternalLink, Save, RotateCw } from 'lucide-react';
import ConfirmPurgeModal from './ConfirmPurgeModal';
import { showToast } from '../utils/toast';
import { STORAGE_KEYS } from '../utils/storage';
import { getAuthData } from '../utils/auth';
import * as logger from '../utils/logger';

const toastOpts = (ref) => ({ anchor: ref, anchorPosition: 'above' });

const History = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [editedAltTexts, setEditedAltTexts] = useState({});
  const [updatingAltText, setUpdatingAltText] = useState({});
  const [regeneratingAltText, setRegeneratingAltText] = useState({});
  const [settings, setSettings] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [5, 10, 25, 50];

  const storeId = sessionStorage.getItem(STORAGE_KEYS.STORE_ID);
  const fetchHistorySettingsInFlightRef = useRef(false);
  const refreshButtonRef = useRef(null);
  const purgeButtonRef = useRef(null);
  const historyActionButtonRef = useRef(null);

  const fetchSettings = async () => {
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          setSettings(settingsData.settings);
        }
      }
    } catch (err) {
      logger.error('Failed to fetch settings:', err);
    }
  };

  const fetchHistory = async () => {
    const opts = toastOpts(refreshButtonRef);
    if (!storeId) {
      setError(t('history.storeIdNotFound'));
      showToast(t('history.storeIdNotFound'), 'error', 3000, opts);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/alttext-history?id=${storeId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setHistory(data.data || []);
      } else {
        const msg = data.error || t('history.failedToFetchHistory');
        setError(msg);
        showToast(msg, 'error', 3000, opts);
      }
    } catch (err) {
      const msg = err.message || t('history.failedToFetchHistory');
      setError(msg);
      showToast(msg, 'error', 3000, opts);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProduct = (productId) => {
    const id = String(productId).split('-')[0];
    const authData = getAuthData();
    if (authData?.store_id) {
      window.open(`https://my.ecwid.com/store/${authData.store_id}#product:mode=edit&id=${id}&return=products`, '_blank');
    }
  };

  const handlePurgeHistory = async () => {
    if (!storeId) return;
    const opts = toastOpts(purgeButtonRef);
    setPurgeLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/alttext-history?id=${storeId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setHistory([]);
        setShowPurgeModal(false);
        showToast(t('history.historyPurged'), 'success', 3000, opts);
      } else {
        const msg = data.error || t('history.failedToPurgeHistory');
        setError(msg);
        showToast(msg, 'error', 3000, opts);
      }
    } catch (err) {
      const msg = err.message || t('history.failedToPurgeHistory');
      setError(msg);
      showToast(msg, 'error', 3000, opts);
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleUpdateAltText = async (item, assetId, event) => {
    if (event?.currentTarget) historyActionButtonRef.current = event.currentTarget;
    const opts = toastOpts(historyActionButtonRef);
    if (!storeId) {
      showToast(t('history.storeIdNotFoundRefresh'), 'error', 3000, opts);
      return;
    }
    const newAltText = editedAltTexts[assetId] ?? item.alt_text;
    if (newAltText === item.alt_text) {
      showToast(t('history.noChangesToSave'), 'info', 3000, opts);
      return;
    }
    setUpdatingAltText(prev => ({ ...prev, [assetId]: true }));
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/products/image/alttext`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: item.product_id.split('-')[0],
          image_id: item.product_id.split('-')[1],
          alt_text: newAltText,
          store_id: storeId,
          asset_id: assetId,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setEditedAltTexts(prev => ({ ...prev, [assetId]: undefined }));
        showToast(t('history.altTextUpdated'), 'success', 3000, opts);
      } else {
        showToast(data.error || t('history.failedToUpdateAltText'), 'error', 3000, opts);
      }
    } catch (err) {
      showToast(err.message || t('history.failedToUpdateAltText'), 'error', 3000, opts);
    } finally {
      setUpdatingAltText(prev => ({ ...prev, [assetId]: false }));
    }
  };

  const handleRegenerateAltText = async (item, assetId, event) => {
    if (event?.currentTarget) historyActionButtonRef.current = event.currentTarget;
    const opts = toastOpts(historyActionButtonRef);
    setRegeneratingAltText(prev => ({ ...prev, [assetId]: true }));
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const productId = item.product_id.split('-')[0];
      const imageId = item.product_id.split('-')[1];
      const response = await fetch(`${apiUrl}/api/bulkproduct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          selectedProductObjects: [{ id: productId, name: item.product_name, brand_id: 0, imageIds: [imageId] }],
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('history.failedToRegenerate'));
      showToast(t('history.regenerationStarted'), 'success', 3000, opts);
      setEditedAltTexts(prev => ({ ...prev, [assetId]: undefined }));
    } catch (err) {
      showToast(err.message || t('history.failedToRegenerate'), 'error', 3000, opts);
    } finally {
      setRegeneratingAltText(prev => ({ ...prev, [assetId]: false }));
    }
  };

  useEffect(() => {
    if (!storeId) return;
    if (fetchHistorySettingsInFlightRef.current) return;
    fetchHistorySettingsInFlightRef.current = true;
    Promise.all([fetchHistory(), fetchSettings()]).finally(() => { fetchHistorySettingsInFlightRef.current = false; });
  }, [storeId]);

  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) return history;
    const q = searchQuery.toLowerCase().trim();
    return history.filter(item => (item.product_name || '').toLowerCase().includes(q));
  }, [history, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedHistory = filteredHistory.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchHistory();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
    <div className="container-fluid py-1">
      <div className="row">
        <div className="col-12">
          <h3 className="mb-3 fw-bold" style={{ fontSize: '1.25rem' }}>{t('history.title')}</h3>
          <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
            {t('history.intro')}
          </p>

          {/* Filters - same style as SEO Optimizer / Products */}
          <div className="card mb-3 border-0 shadow-sm">
            <div className="card-body py-2 px-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="d-flex align-items-center gap-1 text-muted small fw-semibold me-1">
                  <Filter size={14} strokeWidth={2.5} />
                  {t('history.filters')}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ width: 'clamp(140px, 20vw, 220px)' }}
                  placeholder={t('history.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '90px' }}
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  {itemsPerPageOptions.map(n => (
                    <option key={n} value={n}>{t('history.perPage', { count: n })}</option>
                  ))}
                </select>
                <button
                  ref={refreshButtonRef}
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                  onClick={handleRefresh}
                  disabled={isLoading || isRefreshing}
                  title={t('history.refreshTitle')}
                >
                  {isRefreshing || isLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  {t('history.refresh')}
                </button>
                <button
                  ref={purgeButtonRef}
                  type="button"
                  className={`btn btn-sm ms-auto ${history.length > 0 ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                  onClick={() => setShowPurgeModal(true)}
                  disabled={isLoading || !history.length}
                  title={t('history.purgeHistoryTitle')}
                >
                  <Trash2 size={14} className="me-1" />
                  {t('history.purgeHistory')}
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
              <p className="text-muted mt-2 mb-0 small">{t('common.loading')}</p>
            </div>
          ) : paginatedHistory.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5 text-muted">
                {filteredHistory.length === 0 && history.length > 0
                  ? t('history.noSearchResults')
                  : t('history.noHistoryYet')}
              </div>
            </div>
          ) : (
            <div className="card border-0 shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 56 }}>{t('history.tableImage')}</th>
                      <th style={{ minWidth: 140 }}>{t('history.tableProduct')}</th>
                      <th style={{ minWidth: 200 }}>{t('history.tableAltText')}</th>
                      <th style={{ width: 90 }}>{t('history.tableStatus')}</th>
                      <th style={{ width: 130 }}>{t('history.tableDate')}</th>
                      <th style={{ width: 200 }} className="text-end">{t('history.tableActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHistory.map((item) => {
                      const assetId = item.asset_id || item.image_id || item.product_id;
                      const isUpdating = updatingAltText[assetId];
                      const isRegenerating = regeneratingAltText[assetId];
                      const busy = isUpdating || isRegenerating;
                      return (
                        <tr key={assetId}>
                          <td>
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt=""
                                className="rounded"
                                style={{ width: 48, height: 48, objectFit: 'cover', border: '1px solid #eee' }}
                              />
                            ) : (
                              <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                <span className="text-muted small">—</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{item.product_name || '—'}</div>
                            <div className="text-muted small">ID: {item.product_id || '—'}</div>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editedAltTexts[assetId] !== undefined ? editedAltTexts[assetId] : (item.alt_text || '')}
                              onChange={(e) => setEditedAltTexts(prev => ({ ...prev, [assetId]: e.target.value }))}
                              placeholder={t('history.altTextPlaceholder')}
                              disabled={busy}
                              style={{ fontSize: '0.85rem' }}
                            />
                          </td>
                          <td>
                            <span className={`badge ${(item.status || 'COMPLETED') === 'COMPLETED' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.7rem' }}>
                              {item.status || 'COMPLETED'}
                            </span>
                          </td>
                          <td className="small text-muted">
                            {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              {/* <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handleEditProduct(item.product_id)}
                                disabled={busy}
                                title={t('history.verifyTitle')}
                              >
                                <ExternalLink size={12} className="me-1" />
                                {t('history.verify')}
                              </button> */}
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => handleUpdateAltText(item, assetId, e)}
                                disabled={busy}
                                title={t('history.updateTitle')}
                              >
                                {isUpdating ? (
                                  <span className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <>
                                    <Save size={12} className="me-1" />
                                    {t('history.update')}
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => handleRegenerateAltText(item, assetId, e)}
                                disabled={busy}
                                title={t('history.regenerateTitle')}
                              >
                                {isRegenerating ? (
                                  <span className="spinner-border spinner-border-sm" role="status" />
                                ) : (
                                  <>
                                    <RotateCw size={12} className="me-1" />
                                    {t('history.regenerate')}
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredHistory.length > 0 && (
                <div className="d-flex justify-content-between align-items-center border-top px-3 py-2">
                  <div className="small text-muted">
                    {t('history.showingRange', {
                      start: startIndex + 1,
                      end: Math.min(startIndex + itemsPerPage, filteredHistory.length),
                      total: filteredHistory.length,
                    })}
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                        >
                          {t('history.previous')}
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">
                          {currentPage} / {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                        >
                          {t('history.next')}
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    <ConfirmPurgeModal
      isOpen={showPurgeModal}
      title={t('history.purgeModalTitle')}
      message={t('history.purgeModalMessage')}
      confirmText={t('history.purgeModalConfirm')}
      cancelText={t('common.cancel')}
      loading={purgeLoading}
      onConfirm={handlePurgeHistory}
      onCancel={() => setShowPurgeModal(false)}
    />
    </>
  );
};

export default History;
